import React, { useRef, forwardRef, useMemo, useImperativeHandle, useEffect } from 'react'
import type { Editor, Extensions, FocusPosition } from '@tiptap/react'
import { Extension, Node } from '@tiptap/core'
import StarterKit from '@tiptap/starter-kit'
import Color from '@tiptap/extension-color'
import Highlight from '@tiptap/extension-highlight'
import TextStyle from '@tiptap/extension-text-style'
import Link from '@tiptap/extension-link'
import TextAlign from '@tiptap/extension-text-align'
import Underline from '@tiptap/extension-underline'
import HardBreak from '@tiptap/extension-hard-break'
import { useEditor, EditorContent } from '@tiptap/react'
import { TextSelection } from 'prosemirror-state'
import type { Context } from '@paintscout/util/templater'
import CloseIcon from '@material-ui/icons/Close'

import lodashDebounce from 'lodash/debounce'

import { uuid } from '@paintscout/util/builder'
import striptags from 'striptags'
import type { QuoteFile } from 'paintscout'
import type { DropEvent } from 'react-dropzone'
import { PERMITTED_FILE_TYPES } from '@paintscout/util/s3'
import classNames from 'classnames'
import Dropzone from '@paintscout/ui/components/Dropzone'
import Button from '@paintscout/ui/components/Button'
import Typography from '@paintscout/ui/components/Typography'
import InputLabel from '@paintscout/ui/components/InputLabel'
import type { StyleClasses } from '@paintscout/ui/styles'
import { TemplatePlaceholderMark } from './TemplatePlaceholderMark'
import { useEditorStyles } from './useEditorStyles'
import { TipTapToolbar } from './TipTapToolbar'
import { formatPlaceholderLabel } from './formatPlaceholderLabel'

export type EditorMethods = {
  clearContent?: () => void
  getHTML?: () => string
  setHTML?: (value: string) => void
  isDirty?: () => boolean
  focus?: (position?: FocusPosition) => void
}

export type EditorRef = React.MutableRefObject<(Editor & EditorMethods) | null>
export type ForwardedEditorRef = React.ForwardedRef<EditorMethods>

export interface ToolbarConfig {
  bold?: boolean
  italic?: boolean
  underline?: boolean
  color?: boolean
  highlight?: boolean
  headings?: boolean
  lists?: boolean
  align?: boolean
  template?: {
    tooltip?: string
    tags: Array<{
      key: string
      label: string
      hideValue?: boolean
    }>
  }
  link?: boolean
}

export interface EditorProps {
  classes?: StyleClasses<any>
  content: string
  disabled?: boolean
  required?: boolean
  error?: string

  bubbleMenuDisabled?: boolean
  floatingMenuDisabled?: boolean
  menuBarEnabled?: boolean

  label?: React.ReactNode
  sublabel?: React.ReactNode
  className?: string
  characterCount?: boolean

  fullWidth?: boolean
  singleLine?: boolean
  maxHeight?: number
  templateContext?: Context
  toolbar?: ToolbarConfig

  allowHtml?: boolean

  autofocus?: FocusPosition

  Toolbar?: (props: { editor: Editor }) => JSX.Element
  FloatingMenu?: (props: { editor: Editor }) => JSX.Element
  BubbleMenu?: (props: { editor: Editor }) => JSX.Element
  MenuBar?: (props: { editor: Editor }) => JSX.Element

  onChange?: (value: string) => void
  onReset?: () => void
  onAddFiles?: (e?: React.MouseEvent<HTMLDivElement>) => void
  onDrop?: (acceptedFiles: File[], rejectedFiles: File[], event: DropEvent) => void
  onRemoveFile?: (file: QuoteFile) => void
  onEditFile?: (file: QuoteFile) => void
  onSave?: () => void
  onClear?: () => void
  files?: QuoteFile[]
  showFileBar?: boolean
  debounce?: boolean | number
  blockColor?: boolean

  extensions?: Extensions
}

const TipTapEditor = forwardRef((props: EditorProps, ref: React.ForwardedRef<EditorMethods>) => {
  const {
    characterCount,
    extensions = [],
    content,
    label,
    sublabel,
    singleLine,
    disabled,
    blockColor = false,
    debounce: debounceProp = true,
    onChange = () => null,
    onRemoveFile = () => null,
    onSave = null,
    onEditFile = () => null,
    files = [],
    showFileBar = false,
    required,
    onDrop,
    templateContext,
    toolbar,
    error,
    fullWidth: _fullWidth,
    maxHeight: _maxHeight,
    allowHtml: _allowHtml,
    autofocus: _autofocus,
    Toolbar: _Toolbar,
    FloatingMenu: _FloatingMenu,
    BubbleMenu: _BubbleMenu,
    MenuBar: _MenuBar,
    onReset: _onReset,
    onAddFiles: _onAddFiles,
    onClear: _onClear,
    ...otherProps
  } = props

  const debounceTimeout = typeof debounceProp === 'number' ? debounceProp : debounceProp ? 250 : null
  // Unique id to handle lookups w/ multiple editors on same page
  const editorId = useMemo(() => {
    return uuid().split('-')[0]
  }, [])

  const placeholderLabelMap = useMemo(() => {
    return (toolbar?.template?.tags || []).reduce(
      (acc, tag) => {
        acc[tag.key] = tag.label
        return acc
      },
      {} as Record<string, string>
    )
  }, [toolbar?.template?.tags])

  const debouncedOnChange = useMemo(() => {
    return debounceTimeout ? lodashDebounce(onChange, debounceTimeout) : onChange
  }, [])

  const classes = useEditorStyles(props)

  // Check if we're in template editing mode (templateContext values are {{key}} format)
  const isTemplateEditingMode = useMemo(() => {
    if (!templateContext) return true
    const sampleKey = Object.keys(templateContext)[0]
    return sampleKey ? (templateContext[sampleKey] as string) === `{{${sampleKey}}}` : true
  }, [templateContext])

  const editorExtensions = useMemo(() => {
    return [
      Extension.create({
        name: 'shiftTab',
        addKeyboardShortcuts() {
          return {
            ['Shift-Tab']: () => {
              const { editor } = this

              const { from } = editor.state.selection
              const $from = editor.state.selection.$from

              const lineStartPos = from - $from.parentOffset

              const lineText = editor.state.doc.textBetween(lineStartPos, from, ' ')

              if (lineText.startsWith('\t')) {
                editor.commands.deleteRange({ from: lineStartPos, to: lineStartPos + 1 })
                return true
              }

              return true // prevent default behavior
            }
          }
        }
      }),
      Extension.create({
        name: 'literalTab',
        addKeyboardShortcuts() {
          return {
            Tab: () => {
              const { editor } = this
              if (editor.isActive('bulletList') || editor.isActive('orderedList')) {
                return editor.commands.sinkListItem('listItem')
              } else {
                const { from } = editor.state.selection
                const $from = editor.state.selection.$from

                const lineStartPos = from - $from.parentOffset

                editor.commands.insertContentAt({ from: lineStartPos, to: lineStartPos }, '\t')

                const newPos = from + 1
                editor.commands.setTextSelection(newPos)

                return true
              }
            }
          }
        }
      }),
      TextStyle,
      StarterKit.configure({
        hardBreak: false
      }),
      HardBreak.extend({
        addKeyboardShortcuts() {
          return {
            Enter: () => {
              return this.editor.commands.newlineInCode()
            },
            ['Shift-Enter']: () => {
              return this.editor.commands.enter()
            },
            ['Mod-Enter']: () => {
              return this.editor.commands.enter()
            }
          }
        }
      }),
      TextAlign.configure({
        alignments: ['left', 'center', 'right', 'justify'],
        types: ['heading', 'paragraph'],
        defaultAlignment: 'left'
      }),
      Link.configure({
        openOnClick: false
      }),
      // CharacterCount.configure({
      //   limit: characterCount || null
      // }),
      TemplatePlaceholderMark,
      ...(blockColor
        ? []
        : [
            Color,
            Highlight.configure({
              multicolor: true
            })
          ]),
      Underline,
      ...extensions
    ]
  }, [extensions]) as any as Extensions

  const SingleLiner = Node.create({
    name: 'singleLiner',
    topNode: true,
    content: 'block'
  })

  if (singleLine) {
    editorExtensions.unshift(SingleLiner)
  }

  // Reverse function to convert formatted label back to camelCase key
  // e.g., "Customer Name" -> "customerName", "Customer First Name" -> "customerFirstName", "I D" -> "ID"
  const reversePlaceholderLabel = (formatted: string): string => {
    if (!formatted) return formatted
    const words = formatted.trim().split(/\s+/)
    if (words.length === 0) return formatted

    // Handle all-caps single-letter words (e.g., "I D" -> "ID")
    // If all words are single uppercase letters, combine them without lowercasing
    if (words.every((word) => word.length === 1 && word === word.toUpperCase())) {
      return words.join('')
    }

    // Lowercase first word completely, then capitalize first letter of each subsequent word and join
    const firstWord = words[0].toLowerCase()
    const restWords = words.slice(1).map((word) => {
      const lowercased = word.toLowerCase()
      return lowercased.charAt(0).toUpperCase() + lowercased.slice(1)
    })
    return firstWord + restWords.join('')
  }

  // Convert {{key}} to placeholder spans for display in template editing mode
  const processedContent = useMemo(() => {
    if (!content || !isTemplateEditingMode || typeof content !== 'string') return content
    return content.replace(/\{\{(\w+)\}\}/g, (_, key) => {
      const label = placeholderLabelMap[key]
      const displayLabel = formatPlaceholderLabel(key, label)
      const labelAttr = displayLabel ? ` data-label="${displayLabel}"` : ''
      return `<span class="template-placeholder" data-key="${key}"${labelAttr}>${displayLabel}</span>`
    })
  }, [content, isTemplateEditingMode, placeholderLabelMap])

  const editor = useEditor({
    extensions: editorExtensions,
    content: processedContent,
    editable: !disabled,
    editorProps: {
      handleScrollToSelection: (_view) => {
        return true
      },
      handleKeyDown: (view, event) => {
        const { state } = view
        const { selection, doc } = state
        if (!selection.empty) return false

        const { $from } = selection
        const pos = $from.pos

        const getPlaceholderRanges = () => {
          const ranges: Array<{ start: number; end: number; key: string }> = []
          let current: { start: number; end: number; key: string } | null = null
          doc.descendants((node, nodePos) => {
            if (!node.isText) return
            const mark = node.marks?.find((m: any) => m.type.name === 'placeholder')
            if (!mark) {
              current = null
              return
            }
            const key = String(mark.attrs?.key ?? mark.attrs?.label ?? 'placeholder')
            const start = nodePos
            const end = nodePos + node.nodeSize
            if (current && current.key === key && start <= current.end) {
              current.end = end
            } else {
              current = { start, end, key }
              ranges.push(current)
            }
          })
          return ranges
        }

        const ranges = getPlaceholderRanges()
        if (!ranges.length) return false

        const findRangeAt = (position: number) => ranges.find((r) => position > r.start && position < r.end)
        const findNextRange = (position: number) => ranges.find((r) => r.start >= position)
        const findPrevRange = (position: number) => {
          for (let i = ranges.length - 1; i >= 0; i -= 1) {
            if (ranges[i].end <= position) return ranges[i]
          }
          return null
        }

        const currentRange = findRangeAt(pos)

        if (event.key === 'Tab') {
          const targetRange = event.shiftKey ? currentRange || findPrevRange(pos) : currentRange || findNextRange(pos)
          if (targetRange) {
            const target = event.shiftKey ? targetRange.start : targetRange.end
            view.dispatch(state.tr.setSelection(TextSelection.create(doc, target)))
            return true
          }
          return false
        }

        if (event.key === 'ArrowLeft') {
          const targetPos = Math.max(pos - 1, 0)
          const targetRange = findRangeAt(pos) || findRangeAt(targetPos)
          if (targetRange) {
            view.dispatch(state.tr.setSelection(TextSelection.create(doc, targetRange.start)))
            return true
          }
        }

        if (event.key === 'ArrowRight') {
          const targetPos = Math.min(pos + 1, doc.content.size)
          const targetRange = findRangeAt(pos) || findRangeAt(targetPos)
          if (targetRange) {
            view.dispatch(state.tr.setSelection(TextSelection.create(doc, targetRange.end)))
            return true
          }
        }

        return false
      },
      transformPastedHTML(html) {
        // Strip out header tags when pasting
        return striptags(html, [
          'b',
          'u',
          'i',
          'ins',
          'em',
          'br',
          'strong',
          'del',
          'p',
          'ul',
          'ol',
          'li',
          'img',
          'div',
          'span',
          'mark',
          'a'
        ])
      }
    },
    onUpdate: ({ editor }) => {
      const html = editor.getHTML()
      const convert = (h: string) =>
        h
          .replace(
            /<span[^>]*class="[^"]*template-placeholder[^"]*"[^>]*data-key="([^"]+)"[^>]*>.*?<\/span>/g,
            (_, k) => `{{${k}}}`
          )
          .replace(
            /<span[^>]*class="[^"]*template-placeholder[^"]*"[^>]*>([^<]+)<\/span>/g,
            (_, t) => `{{${reversePlaceholderLabel(t.trim())}}}`
          )
      debouncedOnChange?.(isTemplateEditingMode ? convert(html) : html)
    },
    parseOptions: {
      preserveWhitespace: true
    }
  })

  // Disabled sync
  useEffect(() => {
    if (editor) {
      editor.setOptions({
        editable: !disabled
      })
    }
  }, [disabled])

  const toTemplate = (html: string) =>
    html
      .replace(
        /<span[^>]*class="[^"]*template-placeholder[^"]*"[^>]*data-key="([^"]+)"[^>]*>.*?<\/span>/g,
        (_, k) => `{{${k}}}`
      )
      .replace(
        /<span[^>]*class="[^"]*template-placeholder[^"]*"[^>]*>([^<]+)<\/span>/g,
        (_, t) => `{{${reversePlaceholderLabel(t.trim())}}}`
      )
  const toPlaceholder = (html: string) =>
    html.replace(/\{\{(\w+)\}\}/g, (_, k) => {
      const label = placeholderLabelMap[k]
      const displayLabel = formatPlaceholderLabel(k, label)
      const labelAttr = displayLabel ? ` data-label="${displayLabel}"` : ''
      return `<span class="template-placeholder" data-key="${k}"${labelAttr}>${displayLabel}</span>`
    })

  const editorRef: EditorRef = useRef(null)
  const pendingFocusReapply = useRef<(() => void) | null>(null)
  useImperativeHandle(ref, () => ({
    clearContent: () => {
      editorRef.current?.commands?.clearContent()
    },
    getHTML: () => {
      const html = editorRef.current?.getHTML() || ''
      return isTemplateEditingMode ? toTemplate(html) : html
    },
    setHTML: (value: string) => {
      editorRef.current?.commands?.setContent(isTemplateEditingMode ? toPlaceholder(value) : value)
    },
    isDirty: () => {
      const html = editorRef.current?.getHTML() || ''
      return props.content !== (isTemplateEditingMode ? toTemplate(html) : html)
    },
    focus: (position?: FocusPosition) => {
      const editor = editorRef.current
      if (!editor) return

      // Tear down any prior pending reapply so listeners can't stack.
      pendingFocusReapply.current?.()

      // Let TipTap resolve 'start' | 'end' | number and apply scroll/selection.
      editor.chain().focus(position).scrollIntoView().run()

      // Desktop and any case where DOM focus succeeded synchronously: done.
      // iOS Safari restricts programmatic focus on contenteditable to user
      // gestures, so view.focus() is a no-op until the user taps. Re-apply the
      // resolved selection on that first focus event, otherwise iOS will place
      // the caret at the tap location.
      if (editor.isFocused || position === undefined) return

      const targetPos = editor.state.selection.from
      let cleanup = () => {
        /* replaced below — placeholder so prior closures can reference it */
      }
      const reapply = () => {
        cleanup()
        editorRef.current?.commands.setTextSelection(targetPos)
      }
      const timeoutId = setTimeout(() => cleanup(), 2000)
      cleanup = () => {
        clearTimeout(timeoutId)
        editor.off('focus', reapply)
        if (pendingFocusReapply.current === cleanup) pendingFocusReapply.current = null
      }
      pendingFocusReapply.current = cleanup
      editor.on('focus', reapply)
    }
  }))
  const ToolbarComponent = useMemo(() => props.Toolbar ?? TipTapToolbar, [editorRef.current, props])
  if (!editor) return null
  editorRef.current = editor
  const showFileFunctions = showFileBar && (files.length > 0 || (onSave && !editorRef.current.isEmpty))

  return (
    <>
      <div>
        {label ? (
          <InputLabel disabled={disabled} required={required} className={classes.label}>
            {label}
          </InputLabel>
        ) : null}
      </div>

      {sublabel ? (
        <div style={{ padding: '0px 0px 8px 0px' }}>
          <Typography variant="subtitle2">{sublabel}</Typography>
        </div>
      ) : null}

      <Dropzone
        onDrop={onDrop}
        accept={PERMITTED_FILE_TYPES.join(',')}
        buttonFullHeight={false}
        shouldRenderPreview
        noClick
        renderPreview={({ isDragActive }) => {
          return (
            <>
              <div
                className={classNames({
                  [classes.root]: true,
                  [classes.dropRoot]: isDragActive,
                  [classes.withFileBar]: showFileFunctions
                })}
                {...otherProps}
              >
                {ToolbarComponent && editor && <ToolbarComponent editor={editorRef.current} {...props} />}
                <EditorContent
                  editor={editorRef.current}
                  className={classes.editorContent}
                  id={`EditorId-${editorId}`}
                />
                {showFileFunctions && (
                  <div className={classes.fileDivContainer}>
                    <div id="files-container" className={classes.filesContainer}>
                      {files.map((file, index) => {
                        const fileExtension = file.type.includes('/') ? file.type.split('/')[1] : file.type
                        // const unnamedIndex = files.slice(0, index + 1).filter((f) => !f.title).length
                        const displayName = file.title
                          ? file.title.includes(fileExtension)
                            ? file.title
                            : `${file.title}.${fileExtension}`
                          : `Untitled file`
                        return (
                          <div key={index} className={classes.fileItem}>
                            <div
                              className={classes.fileItemTextDiv}
                              onClick={() => (disabled ? null : onEditFile(file))}
                            >
                              <Typography variant="body1" className={classes.fileItemText}>
                                {displayName}
                              </Typography>
                            </div>
                            <CloseIcon
                              onClick={() => (disabled ? null : onRemoveFile(file))}
                              className={classes.fileCloseIcon}
                            />
                          </div>
                        )
                      })}
                    </div>
                    {onSave && (
                      <Button
                        variant="hoverUnderline"
                        color="primary"
                        onClick={() => onSave()}
                        className={classes.saveButton}
                        disabled={disabled}
                      >
                        Save
                      </Button>
                    )}
                  </div>
                )}
              </div>
              {characterCount && (
                <div className={classes.characterCountDiv}>
                  <Typography variant="subtitle2">
                    {`Characters: ${editorRef.current ? editorRef.current.getText().length : 0}`}
                  </Typography>
                </div>
              )}
              {error && (
                <div className={classes.errorDiv}>
                  <Typography variant="subtitle2" color="error">
                    {error}
                  </Typography>
                </div>
              )}
            </>
          )
        }}
        {...props}
      />
    </>
  )
})
TipTapEditor.displayName = 'TipTapEditor'

export { TipTapEditor as Editor }
export type { Editor as TipTapEditor }
